<script lang="ts" setup>
import type { OtTabType } from '@1trading/design-system';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { TradingChartContainer } from '@exchange/libs/charting/ui/src';
import { marketService } from '@exchange/libs/market/service/src';
import MarketHeaderDetailsMobile from '@exchange/libs/market/ui/src/lib/components/MarketHeaderDetailsMobile.vue';
import MarketHeaderMobile from '@exchange/libs/market/ui/src/lib/components/MarketHeaderMobile.vue';
import MyOrdersContainer from '@exchange/libs/order/my-orders/ui/src/lib/MyOrdersContainer.vue';
import { OrderContainer } from '@exchange/libs/order/order-form/ui/src';
import OrderBuySellPanel from '@exchange/libs/order/order-form/ui/src/lib/OrderBuySellPanel.vue';
import { DepthChartContainer } from '@exchange/libs/orderbook/depth-chart/ui/src';
import OrderbookContainer from '@exchange/libs/orderbook/orderbook/ui/src/lib/OrderbookContainer.vue';
import { PriceTicksHistoryContainer } from '@exchange/libs/price-ticks/ui/src';
import RibbonSection from '@exchange/libs/ribbons/RibbonSection.vue';
import { currencyService } from '@exchange/libs/utils/currency/src';

import HeaderMobile from '../app-header/HeaderMobile.vue';

const props = defineProps({
  accountId: { type: String, required: false },
  marketId: { type: String, required: true },
});
const emit = defineEmits<(e: 'go-back') => void>();

const { t } = useI18n({ useScope: 'global' });

const { getMarket } = marketService;
const { defaultFiatCurrency, currencies } = currencyService;

const tradeContent = ref<HTMLElement>();

const market = computed(() => getMarket(props.marketId));

const mobileTradingPageSlides = {
  trade: 'trade',
  depth: 'depth',
  history: 'history',
  myorders: 'myorders',
} as const;
type MobileTradingPageSlide = (typeof mobileTradingPageSlides)[keyof typeof mobileTradingPageSlides];

const chartsTypes = {
  depth: 'depth',
  tv: 'tv',
} as const;
type ChartsType = (typeof chartsTypes)[keyof typeof chartsTypes];

const getTabs = () => [
  {
    getTitle: () => t('modules.singleMarketSlider.trade'),
    id: mobileTradingPageSlides.trade,
    active: true,
  },
  {
    getTitle: () => 'Chart',
    id: mobileTradingPageSlides.depth,
    active: false,
  },
  {
    getTitle: () => t('modules.singleMarketSlider.history'),
    id: mobileTradingPageSlides.history,
    active: false,
  },
  {
    getTitle: () => 'Portfolio',
    id: mobileTradingPageSlides.myorders,
    active: false,
  },
];

const tabs = ref<Array<OtTabType<MobileTradingPageSlide>>>(getTabs());
const activeTab = computed(() => tabs.value.find((e) => e.active)?.id);

const chartTabs = ref<Array<OtTabType<ChartsType>>>([
  {
    getTitle: () => t('modules.singleMarketSlider.tradingView'),
    id: chartsTypes.tv,
    active: true,
  },
  {
    getTitle: () => t('modules.singleMarketSlider.depth'),
    id: chartsTypes.depth,
    active: false,
  },
]);
const activeChartTab = computed(() => chartTabs.value.find((e) => e.active)?.id);

const moveBack = ref(false);
const prevTav = ref<MobileTradingPageSlide>(mobileTradingPageSlides.trade);

const onTabSelected = (tab: OtTabType<MobileTradingPageSlide>) => {
  const currentTabIndex = tabs.value.findIndex((tb) => tb.id === tab.id);
  const previousTabIndex = tabs.value.findIndex((tb) => tb.id === prevTav.value);

  // If current tab is to the left of previous tab, slide back (right to left)
  moveBack.value = currentTabIndex < previousTabIndex;
  prevTav.value = tab.id;
};

const onGoBack = () => emit('go-back');

const onScrollContent = (top: number) => {
  if (!tradeContent.value) {
    return;
  }

  const contentTop = tradeContent.value.getBoundingClientRect().top;

  tradeContent.value.scroll({
    top: top - contentTop,
    behavior: 'smooth',
  });
};
</script>

<template>
  <div class="spot-exchange-mobile-view">
    <ribbon-section :market="market" />
    <header-mobile
      class="spot-exchange-mobile-view__header"
      :wide-main-content="true"
      @go-back="onGoBack"
    >
      <template #top-content>
        <market-header-mobile
          v-if="market"
          :market="market"
        />
      </template>
      <template #main-content>
        <ot-tabs
          v-model:tabs="tabs"
          class="spot-exchange-mobile-view__tabs"
          :dense="false"
          :scrollable="true"
          @tab-selected="onTabSelected"
        />
        <market-header-details-mobile
          v-if="market"
          :market="market"
          :currencies="currencies"
          :default-fiat-currency="defaultFiatCurrency"
        />
      </template>
    </header-mobile>
    <x-transition-slide
      :push="true"
      :is-xs="true"
      :move-back="moveBack"
    >
      <div
        v-if="activeTab === mobileTradingPageSlides.trade"
        ref="tradeContent"
        class="spot-exchange-mobile-view__trade-container"
      >
        <div class="spot-exchange-mobile-view__trade-actions">
          <div class="spot-exchange-mobile-view__order-form">
            <order-container
              :market-id="marketId"
              :transparent-background="true"
            />
          </div>
          <div class="spot-exchange-mobile-view__order-book">
            <orderbook-container
              :market-id="marketId"
              :mini="true"
              :show-title="false"
            />
          </div>
        </div>
      </div>
      <div
        v-if="activeTab === mobileTradingPageSlides.depth"
        class="spot-exchange-mobile-view__charting-container"
      >
        <div class="spot-exchange-mobile-view__tabs spot-exchange-mobile-view__tabs--charting">
          <ot-tabs
            v-model:tabs="chartTabs"
            :dense="false"
          />
        </div>
        <div class="spot-exchange-mobile-view__chart">
          <trading-chart-container
            v-if="activeChartTab === chartsTypes.tv"
            :key="accountId"
            :market-id="marketId"
          />
          <depth-chart-container
            v-if="activeChartTab === chartsTypes.depth"
            :market-id="marketId"
          />
        </div>
        <order-buy-sell-panel :market-id="marketId" />
      </div>
      <div
        v-if="activeTab === mobileTradingPageSlides.history"
        class="spot-exchange-mobile-view__history-container"
      >
        <price-ticks-history-container
          :market-id="marketId"
          :show-title="false"
        />
      </div>
      <div
        v-if="activeTab === mobileTradingPageSlides.myorders"
        class="spot-exchange-mobile-view__my-orders-container"
      >
        <my-orders-container
          :key="`${accountId}-${subaccountType}`"
          :market-id="marketId"
          @my-orders:scroll-content="onScrollContent"
        />
      </div>
    </x-transition-slide>
  </div>
</template>

<style lang="scss">
.spot-exchange-mobile-view {
  display: flex;
  height: 100%;
  flex: 1 1 auto;
  flex-direction: column;

  &__header {
    --header-mobile-custom-bg: rgb(var(--v-theme-elevation-1));

    flex: 0 0 auto;
    border-bottom: 3px solid rgb(var(--v-theme-elevation-0));
  }

  &__tabs {
    margin-bottom: 8px;

    &--charting {
      padding: 0 16px;
    }
  }

  &__trade-container,
  &__history-container {
    overflow: auto;
  }

  &__chart {
    height: calc(100% - var(--app-mobile-buy-sell-panel-trigger-height));
  }

  &__trade-actions {
    display: grid;
    height: 100%;
    gap: 1px;
    grid-template-columns: 1.35fr 1fr;
  }

  &__charting-container {
    display: flex;
    flex-direction: column;
  }
}
</style>
